<script lang="ts">
  import { INDICATOR_OPTIONS } from "@/entities/report/report.constants";

  // KPI indicators applied to a Bayesian comparison by default — it skips the
  // indicators step, so these are used on submit instead.
  const BAYESIAN_INDICATOR_NAMES = ["delivers", "actions", "clicks", "sends", "approvedSum"];
  export const BAYESIAN_DEFAULT_INDICATORS = INDICATOR_OPTIONS.filter((option) =>
    BAYESIAN_INDICATOR_NAMES.includes(option.name),
  ).map((option) => option.key);
</script>

<script setup lang="ts">
  import { VCheckBox } from "@krainovsd/vue-ui";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import Card from "@/components/atoms/Card.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const { t } = useI18n();

  // Optional overrides so the step can be reused (e.g. the comparison wizard)
  // with different wording; default to the report-wizard text.
  const props = defineProps<{ title?: string; subtitle?: string; submitLabel?: string }>();

  const selectedIndicators = defineModel<Set<number>>({ required: true });
  const emit = defineEmits<{ submit: []; back: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const indicatorOptions = computed(() =>
    INDICATOR_OPTIONS.map((option) => ({
      key: option.key,
      label: t(option.title.path, {}, { default: option.title.default }),
    })),
  );

  // At least one indicator must be selected.
  const valid = computed(() => selectedIndicators.value.size > 0);

  function toggleIndicator(key: number) {
    const next = new Set(selectedIndicators.value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selectedIndicators.value = next;
  }
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="
      props.title ?? $t('page.createReport.step5.title', {}, { default: 'Выберите показатели' })
    "
    :subtitle="
      props.subtitle ??
      $t('page.createReport.step5.subtitle', {}, { default: 'Метрики, которые попадут в отчёт' })
    "
    back
    submit
    :disabled="!valid"
    :submit-label="props.submitLabel"
    @back="emit('back')"
    @submit="emit('submit')"
  >
    <div :class="$style.indicators">
      <Card
        v-for="indicator in indicatorOptions"
        :key="indicator.key"
        :class="$style.indicatorCard"
        @click="toggleIndicator(indicator.key)"
      >
        <VCheckBox
          :model-value="selectedIndicators.has(indicator.key)"
          @click.stop
          @update:model-value="toggleIndicator(indicator.key)"
        >
          {{ indicator.label }}
        </VCheckBox>
      </Card>
    </div>
  </StepLayout>
</template>

<style lang="scss" module>
  .indicators {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .indicatorCard {
    padding: 0.75rem 1rem;

    display: flex;
    align-items: center;

    cursor: pointer;
  }
</style>
